import React, { Dispatch, SetStateAction, useState } from 'react'
import i18n from 'live/services/i18n'
import styled from 'styled-components'
import SearchBar from 'live/components/ui/SearchBar'
import { ACTION_PLAN_STATUS } from '../../common/utils'
import MaskedInput from 'react-maskedinput'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import moment from 'moment'
import LiveTooltip from 'live/components/ui/tooltip'

interface IProps {
    searchFilter: string
    setSearchFilter: Dispatch<SetStateAction<string>>
    selectedSpan: string
    setSelectedSpan: Dispatch<SetStateAction<string>>
    setActionPlansView: Dispatch<SetStateAction<ActionPlanStatusFilter>>
    actionPlansView: ActionPlanStatusFilter
}
export function AnalysisFilters({
    searchFilter,
    setSearchFilter,
    selectedSpan,
    setSelectedSpan,
    actionPlansView,
    setActionPlansView
}: IProps): JSX.Element {
    const StatusOptions: ActionPlanStatusFilter[] = ['all', ACTION_PLAN_STATUS.PENDING, ACTION_PLAN_STATUS.DONE]
    const preSelectedSpans = ['last 24 hours', 'last 7 days', 'current month']
    const [startDate, setStartDate] = useState<string>('')
    const [endDate, setEndDate] = useState<string>('')

    function onSubmitCustomSpan(): void {
        setSelectedSpan(`${startDate} to ${endDate}`)
    }

    function onOptionClick(option: string): void {
        setSelectedSpan(option)
        const activeElement = document.activeElement as HTMLElement
        activeElement?.blur()
    }

    function getDiffBetweenDates(): number {
        const start = moment(startDate, 'YYYYMMDD')
        const end = moment(endDate, 'YYYYMMDD')

        return end.diff(start, 'days')
    }

    const diffBetweenDates = getDiffBetweenDates()
    const diffIsNegative = diffBetweenDates < 0
    const diffIsGreaterThan3Months = diffBetweenDates > 92

    const errorInStartSpan = startDate.includes('_')
    const errorInEndSpan = endDate.includes('_')

    function getErrorMessage(): string {
        if (!startDate || !endDate) return i18n('Please enter a start and end date to view a custom span')
        if (diffIsNegative) return i18n('Please make sure start date is before end date')
        if (diffIsGreaterThan3Months) return i18n('Plese choose a span shorter than 3 months')
        if (errorInStartSpan || !startDate) {
            return `${i18n('Please fill start date correctly')} Ex: ${i18n('YYYY-MM-DD')}`
        }
        if (errorInEndSpan || !endDate) return `${i18n('Please fill end date correctly')} Ex: ${i18n('YYYY-MM-DD')}`
        else return ''
    }
    const errorMessage = getErrorMessage()

    return (
        <StyledAnalysisFilters className={'analysisListFilter'}>
            <SearchBar filter={searchFilter} setFilter={setSearchFilter} />
            <div tabIndex={1} className={`analysisListFilter--span`}>
                <div className={`analysisListFilter--span__selected`}>
                    {i18n('span')}: <strong>{i18n(selectedSpan)}</strong>
                    <i className="fas fa-sort-down" />
                </div>
                <div className={'analysisListFilter--span__options'}>
                    <div className={'analysisListFilter--span__options--inputs'}>
                        <label className={'analysisListFilter--span__options--inputs__start'}>
                            {i18n('Start')}
                            <MaskedInput
                                mask="1111-11-11"
                                type="text"
                                value={startDate}
                                onChange={event => setStartDate(event.target.value)}
                                className={errorInStartSpan ? 'error' : ''}
                                placeholder={i18n('YYYY-MM-DD')}
                            />
                        </label>
                        <label className={'analysisListFilter--span__options--inputs__end'}>
                            {i18n('End')}

                            <MaskedInput
                                mask="1111-11-11"
                                type="text"
                                value={endDate}
                                onChange={event => setEndDate(event.target.value)}
                                className={errorInEndSpan ? 'error' : ''}
                                placeholder={i18n('YYYY-MM-DD')}
                            />
                        </label>
                        <LiveTooltip position="left" content={(errorMessage as unknown) as Node} hide={!errorMessage}>
                            <div>
                                <SubmitOrCancel
                                    onSubmit={() => onSubmitCustomSpan()}
                                    saving={false}
                                    disabled={errorMessage}
                                    hideCancel={true}
                                    buttonMessage={i18n('Apply')}
                                />
                            </div>
                        </LiveTooltip>
                    </div>
                    <ul className={'analysisListFilter--span__options--list'}>
                        {preSelectedSpans.map(spanOption => {
                            const isSelected = spanOption === selectedSpan

                            return (
                                <li className={`analysisListFilter--span__options--list__option `} key={spanOption}>
                                    <button
                                        className={`analysisListFilter--span__options--list__option--button ${
                                            isSelected ? 'isSelected' : ''
                                        }`}
                                        onClick={() => onOptionClick(spanOption)}
                                    >
                                        {i18n(spanOption)}
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </div>
            <div className={`analysisListFilter--status`} tabIndex={1}>
                <div className={`analysisListFilter--status__selected`}>
                    {i18n('Action Plans')}: <strong>{i18n(actionPlansView)}</strong>
                    <i className="fas fa-sort-down" />
                </div>

                <ul className={`analysisListFilter--status__optionList`}>
                    {StatusOptions.map(option => {
                        return (
                            <li className={`analysisListFilter--status__optionList--option`} key={option}>
                                <button
                                    className={`analysisListFilter--status__optionList--option__button`}
                                    onClick={() => setActionPlansView(option)}
                                >
                                    {i18n(option)}
                                </button>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </StyledAnalysisFilters>
    )
}

const StyledAnalysisFilters = styled.div`
    --border: 1px solid var(--gray-300);
    border: var(--border);
    display: flex;
    align-items: center;
    > div:first-child {
        flex-grow: 1;
        box-shadow: none;
        * {
            box-shadow: none;
        }
    }

    .analysisListFilter {
        &--status,
        &--span {
            background: transparent;
            font-size: 15px;
            border-left: var(--border);
            position: relative;
            transition: all 0.3s ease-in-out;
            padding: 8px 16px;
            min-width: 205px;
            color: var(--gray-700);
            &:hover,
            &:focus {
                text-decoration: none;
                background: ${({ theme }) => theme['blue-700']};
                &,
                i {
                    color: ${({ theme }) => theme['gray-0']};
                }
            }
            &:focus-within {
                i {
                    transform: translateY(10%) rotate(180deg);
                }
            }

            &__selected {
                text-transform: capitalize;
                display: flex;
                align-items: center;
                strong {
                    flex-grow: 1;
                }
                strong,
                i {
                    margin-left: 8px;
                }
                i {
                    transition: all 0.2s ease-in-out;
                    color: var(--blue-500);
                    transform: translateY(-20%);
                    justify-self: flex-end;
                }
            }
            &__options,
            &__optionList {
                display: none;
                position: absolute;
                bottom: -5px;
                left: 0;
                background: ${({ theme }) => theme['gray-0']};
                border-radius: 3px;
                box-shadow: 0px 0px 4px rgba(54, 65, 89, 0.25);
                font-size: 18px;
                width: 100%;
                padding: 16px 0;
                align-items: flex-start;
                margin: 0;
                transform: translateY(100%);
            }
        }
        &--span {
            min-width: 330px;
            position: relative;
            &:focus-within,
            &:focus {
                div.analysisListFilter--span__options {
                    display: flex;
                }
            }
            &__options {
                padding: 16px;
                &--inputs {
                    padding-right: 8px;
                    margin-right: 8px;
                    border-right: 1px solid var(--gray-500);
                    display: flex;
                    flex-direction: column;
                    &__start,
                    &__end {
                        margin-bottom: 15px;
                    }
                }
                &--list {
                    min-width: 150px;
                    &__option {
                        &--button {
                            background: transparent;
                            border: none;
                            border-radius: 50px;
                            padding: 7px;
                            margin-bottom: 5px;
                            color: var(--gray-700);
                            font-size: 14px;
                            text-transform: capitalize;
                            transition: all 0.2s ease-in-out;
                            &:hover,
                            &.isSelected {
                                color: var(--gray-0);
                                background: var(--gray-500);
                            }
                        }
                    }
                }
            }
        }
        &--status {
            &:focus-within,
            &:focus {
                ul.analysisListFilter--status__optionList {
                    display: flex;
                }
            }
            &__optionList {
                flex-direction: column;

                &--option {
                    margin-bottom: 3px;
                    width: 100%;
                    &__button {
                        background: none;
                        border: none;
                        width: 100%;
                        padding: 8px 16px;
                        display: flex;
                        color: ${({ theme }) => theme['blue-500']};
                        text-transform: capitalize;

                        &:hover,
                        &:focus {
                            background: ${({ theme }) => theme['blue-200']};
                            text-decoration: none;
                        }
                    }
                }
            }
        }
    }
`
